import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TrackByFunction, ViewEncapsulation } from '@angular/core';

import { RDS_DATA_DICTIONARY_ALL_COLUMNS, RDS_DATA_DICTIONARY_COLUMNS, RdsDataDictionaryTableColumn } from './column.model';

export class RdsColumnToggleEvent {
  /**
   * @param show Whether the column should be shown
   * @param column The column to show or hide
   */
  constructor(public show: boolean, public column: RdsDataDictionaryTableColumn) {}
}

// ERROR Error: ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.
// Previous value: 'aria-expanded: true'.Current value: 'aria-expanded: null'.
// https://github.com/angular/components/issues/14440
@Component({
  selector: 'rds-c-column-toggle',
  template: `
    <button i18n-aria-label aria-label="Displayed columns" mat-icon-button [matMenuTriggerFor]="menu">
      <mat-icon>{{ menuButtonIcon }}</mat-icon>
    </button>
    <mat-menu #menu="matMenu" i18n-aria-label aria-label="Displayed table columns">
      <div
        *ngFor="let column of columnMenuOptions; trackBy: _trackByProperty"
        mat-menu-item
        role="menuitemcheckbox"
        [attr.aria-checked]="selection.selected.length | isColumnSelected: selection:column"
        (click)="$event.stopPropagation(); handleColumnSelectionChange(column)"
        (keydown.enter)="$event.stopPropagation(); handleColumnSelectionChange(column)"
        (keydown.space)="$event.stopPropagation(); handleColumnSelectionChange(column)"
      >
        <mat-icon [color]="(selection.selected.length | isColumnSelected: selection:column) ? 'accent' : undefined">{{
          (selection.selected.length | isColumnSelected: selection:column) ? 'check_box' : 'check_box_outline_blank'
        }}</mat-icon>
        {{ column.label }}
      </div>
    </mat-menu>
  `,
  styles: [
    `
      .rds-c-column-toggle-menu-selection-list,
      .rds-c-column-toggle-menu-selection-list > .mat-menu-item {
        padding: 0;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsColumnToggleComponent {
  /**
   * All the column options to choose from. Defaults to:
   * `RDS_DATA_DICTIONARY_ALL_COLUMNS.slice()`
   */
  @Input() allColumnOptions: Array<RdsDataDictionaryTableColumn> | null | undefined = RDS_DATA_DICTIONARY_ALL_COLUMNS.slice();
  /** Icon to use for the menu button */
  @Input() menuButtonIcon: string | null | undefined = 'more_vert';
  /**
   * The selected columns. Defaults to:
   * `RDS_DATA_DICTIONARY_ALL_COLUMNS.slice(0, 5)`
   */
  @Input()
  get selectedColumns() {
    return this._selectedColumns;
  }
  set selectedColumns(s: Array<RdsDataDictionaryTableColumn> | null | undefined) {
    this._selectedColumns = s;
    this.selection.clear();
    this.selection.select(...(s ?? []).map((c) => c.property));
  }
  private _selectedColumns: Array<RdsDataDictionaryTableColumn> | null | undefined = this.columnMenuOptions.slice(0, 5);

  /** Emitted when a column is toggled on or off */
  @Output() columnToggle = new EventEmitter<RdsColumnToggleEvent>();

  /** The columns to toggle in the menu. Exclude the selected column. */
  get columnMenuOptions(): Array<RdsDataDictionaryTableColumn> {
    return this.allColumnOptions?.filter((c) => c.property !== 'selected' && c.property !== 'editButton') ?? [];
  }

  /** Manages the selected options for handling the menu-items' icon state & toggling */
  selection = new SelectionModel<RDS_DATA_DICTIONARY_COLUMNS>(true, this.selectedColumns?.map((sc) => sc.property) ?? []);

  handleColumnSelectionChange(column: RdsDataDictionaryTableColumn) {
    this.columnToggle.emit(new RdsColumnToggleEvent(!this.selection.isSelected(column.property), column));
  }

  _trackByProperty: TrackByFunction<RdsDataDictionaryTableColumn> = (index, column) => column.property;
}
